import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ContentManager } from "@/components/admin/ContentManager";
import { ForumManager } from "@/components/admin/ForumManager";
import { AudiobookManager } from "@/components/admin/AudiobookManager";
import { Video, FileUp, ShoppingBag, GraduationCap, Loader2, ShieldCheck, CheckCircle2, AlertTriangle, RefreshCw, ExternalLink, Globe2, Star, Clock, Mail, Upload, Newspaper, Trash2, Lock, Megaphone, BarChart3, MessageSquare, Lightbulb, BookOpen, Pencil, Layers, MessagesSquare, Headphones, HeartPulse, Share2, Bell, Copy, KeyRound } from "lucide-react";

const CATS = [
  ["kemetic", "Kemetic"], ["nuwaupian", "Nuwaupian"], ["noi", "Nation of Islam"], ["moorish", "Moorish"],
  ["hebrew", "Hebrew Israelite"], ["metaphysical", "Metaphysical"], ["panafrican", "Pan-African"],
  ["esoteric", "Esoteric"], ["astro", "Astrology"],
];

function extractYT(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
  return m ? m[1] : null;
}

const field = "w-full bg-black/50 border border-white/10 px-4 py-2.5 text-bone focus:outline-none focus:border-gold/50 text-sm";
const lbl = "text-xs uppercase tracking-widest text-bone/60 mb-1.5 block";

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("analytics");

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "admin") { toast.error("Admin access only"); nav("/"); }
  }, [user, loading, nav]);
  if (loading || !user) return <div className="bg-onyx min-h-screen pt-32 text-center text-bone/50">Loading...</div>;
  if (user.role !== "admin") return <div className="bg-onyx min-h-screen pt-32 text-center text-bone/50" data-testid="admin-forbidden">Admin access only. Redirecting...</div>;

  const tabs = [["analytics", "Campaign Analytics", BarChart3], ["feedback", "Feedback", MessageSquare], ["forum", "Community Forum", MessagesSquare], ["video", "Import Video", Video], ["audiobook", "Audiobooks", Headphones], ["healing", "Health & Healing", HeartPulse], ["categories", "Categories", Layers], ["insight", "In-Sight", Lightbulb], ["reading", "Foundational Texts", BookOpen], ["article", "Post Article", Newspaper], ["ticker", "Ticker", Megaphone], ["community", "Community Links", Share2], ["notifications", "Reply Alerts", Bell], ["file", "Upload File", FileUp], ["product", "Add Product", ShoppingBag], ["class", "Add Class", GraduationCap], ["verify", "Verify Credentials", Globe2], ["subscribers", "Subscribers", Mail], ["health", "Link Health", ShieldCheck], ["security", "Change Password", Lock]];

  return (
    <main className="bg-onyx min-h-screen pt-16" data-testid="admin-page">
      <div className="border-b border-white/10 grain bg-surface">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-14">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Credentialed Import Center</p>
          <h1 className="font-serif text-4xl md:text-5xl text-bone">Admin Panel</h1>
          <p className="text-bone/50 mt-2">Welcome, {user.name}. Import videos, upload files and manage the store.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 md:px-8 py-12">
        <div className="flex flex-wrap gap-2 mb-10">
          {tabs.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm transition-colors ${tab === k ? "bg-gold text-black" : "border border-white/15 text-bone/70 hover:border-gold/40"}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {tab === "analytics" && <AnalyticsPanel />}
        {tab === "feedback" && <FeedbackPanel />}
        {tab === "forum" && <ForumManager />}
        {tab === "video" && <VideoForm />}
        {tab === "audiobook" && <AudiobookManager />}
        {tab === "healing" && <HealingForm />}
        {tab === "categories" && <CategoryManager />}
        {tab === "insight" && <InsightForm />}
        {tab === "reading" && <ReadingForm />}
        {tab === "article" && <ArticleForm />}
        {tab === "ticker" && <TickerForm />}
        {tab === "community" && <CommunityForm />}
        {tab === "notifications" && <NotificationsForm />}
        {tab === "file" && <FileForm />}
        {tab === "product" && <ProductForm />}
        {tab === "class" && <ClassForm />}
        {tab === "verify" && <VerifyCredentials />}
        {tab === "subscribers" && <Subscribers />}
        {tab === "health" && <LinkHealth />}
        {tab === "security" && <ChangePassword />}
      </div>
    </main>
  );
}

const parseSources = (text) =>
  (text || "").split("\n").map((raw) => {
    const line = (raw || "").trim();
    if (!line) return null;
    const parts = line.split("|").map((s) => (s || "").trim());
    let label, url, kind;
    if (parts.length >= 2) {
      [label, url, kind] = parts;
    } else {
      url = parts[0]; // bare URL pasted on its own line
    }
    if (!url) return null;
    if (!/^https?:\/\//i.test(url)) {
      if (!url.includes(".")) return null; // not a URL, skip
      url = `https://${url}`;
    }
    if (!label) {
      try { label = new URL(url).hostname.replace(/^www\./, ""); } catch { label = url; }
    }
    return { label, url, kind: kind || "reference" };
  }).filter(Boolean);

function VideoForm() {
  return (
    <ContentManager
      endpoint="/videos"
      heading={<><Video size={22} className="text-gold" /> Import / Manage Videos</>}
      description="Paste any YouTube / social media link. YouTube links play inline automatically. Edit or remove any imported video below."
      testid="admin-video"
      blank={{ url: "", title: "", teacher: "", category: "kemetic", duration: "", description: "" }}
      fields={[
        { key: "url", label: "Video URL", required: true, full: true, placeholder: "https://youtube.com/watch?v=..." },
        { key: "title", label: "Title", required: true },
        { key: "teacher", label: "Teacher", required: true },
        { key: "category", label: "Category", type: "select", options: CATS },
        { key: "duration", label: "Duration", placeholder: "45:30" },
        { key: "description", label: "Description", type: "textarea" },
      ]}
      toPayload={(f) => ({
        title: f.title, teacher: f.teacher, category: f.category, description: f.description,
        duration: f.duration, youtube_id: extractYT(f.url), external_url: f.url,
      })}
      fromItem={(it) => ({
        url: it.external_url || (it.youtube_id ? `https://www.youtube.com/watch?v=${it.youtube_id}` : ""),
        title: it.title || "", teacher: it.teacher || "", category: it.category || "kemetic",
        duration: it.duration || "", description: it.description || "",
      })}
      primary={(it) => it.title}
      secondary={(it) => `${it.teacher} · ${it.category}`}
    />
  );
}

function InsightForm() {
  return (
    <ContentManager
      endpoint="/insights"
      heading={<><Lightbulb size={22} className="text-gold" /> In-Sight — Consciousness Concepts</>}
      description="These concepts appear in the homepage In-Sight section. Give each one a Recommended Reading."
      testid="admin-insight"
      blank={{ title: "", concept: "", description: "", recommended_reading: "" }}
      fields={[
        { key: "title", label: "Title", required: true },
        { key: "concept", label: "Concept (tag)", required: true, placeholder: "Self-Knowledge" },
        { key: "description", label: "Description", type: "textarea", required: true },
        { key: "recommended_reading", label: "Recommended Reading", full: true, placeholder: "Book Title — Author" },
      ]}
      primary={(it) => it.title}
      secondary={(it) => it.recommended_reading ? `Reading: ${it.recommended_reading}` : it.concept}
    />
  );
}

function ReadingForm() {
  const COLORS = [
    ["from-red-800 to-red-950", "Red"], ["from-green-800 to-emerald-950", "Green"],
    ["from-amber-700 to-yellow-950", "Amber"], ["from-yellow-700 to-amber-900", "Gold"],
    ["from-stone-700 to-stone-950", "Stone"], ["from-orange-800 to-red-950", "Orange"],
    ["from-neutral-800 to-black", "Black"],
  ];
  return (
    <ContentManager
      endpoint="/readings"
      heading={<><BookOpen size={22} className="text-gold" /> Foundational Texts</>}
      description="Recommended reading books shown in the homepage Foundational Texts section."
      testid="admin-reading"
      blank={{ title: "", author: "", image_color: "from-amber-700 to-yellow-950" }}
      fields={[
        { key: "title", label: "Title", required: true },
        { key: "author", label: "Author", required: true },
        { key: "image_color", label: "Cover Color", type: "select", options: COLORS, full: true },
      ]}
      primary={(it) => it.title}
      secondary={(it) => it.author}
    />
  );
}


function FileForm() {
  const [f, setF] = useState({ title: "", category: "General", description: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/library").then((r) => setDocs(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose a file");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("title", f.title); fd.append("category", f.category); fd.append("description", f.description);
    try {
      await api.post("/library", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded to the archive");
      setF({ title: "", category: "General", description: "" }); setFile(null);
      e.target.reset();
      load();
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  const del = async (doc) => {
    if (!window.confirm(`Permanently delete the document "${doc.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/library/${doc.id}`); toast.success("Document deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  return (
    <div data-testid="admin-file">
      <h2 className="font-serif text-2xl text-bone mb-2 flex items-center gap-2"><FileUp size={22} className="text-gold" /> Documents & Downloads</h2>
      <p className="text-bone/50 text-sm mb-6">Upload PDFs, documents or any file for the downloadable library. Remove any document below.</p>
      <form onSubmit={submit} className="bg-surface border border-white/10 p-8 space-y-5 max-w-2xl mb-10" data-testid="admin-file-form">
        <div><label className={lbl}>File</label><input type="file" required onChange={(e) => setFile(e.target.files[0])} className={field} data-testid="admin-file-input" /></div>
        <div><label className={lbl}>Title</label><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={field} data-testid="admin-file-title" /></div>
        <div><label className={lbl}>Category</label><input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={field} /></div>
        <div><label className={lbl}>Description</label><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={field} rows={3} /></div>
        <SubmitBtn busy={busy} label="Upload File" testid="admin-file-submit" />
      </form>

      <h3 className="font-serif text-xl text-bone mb-3">Documents ({docs.length})</h3>
      {loading ? (
        <div className="text-bone/50 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading...</div>
      ) : docs.length === 0 ? (
        <p className="text-bone/50 text-sm" data-testid="admin-file-empty">No documents uploaded yet.</p>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 p-3 bg-surface text-sm" data-testid="admin-file-row">
              <span className="text-[10px] uppercase tracking-widest text-bone/40 border border-white/10 px-1.5 py-0.5">{d.file_type}</span>
              <div className="flex-1 min-w-0">
                <p className="text-bone truncate">{d.title}</p>
                <p className="text-bone/40 text-xs truncate">{d.category} · {d.size_kb} KB</p>
              </div>
              <button onClick={() => del(d)} className="text-bone/50 hover:text-red-400" data-testid="admin-file-delete"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryManager() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/categories").then((r) => setCats(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const del = async (c) => {
    const n = c.video_count || 0;
    const warning = n > 0
      ? `⚠ The category "${c.name}" contains ${n} video${n === 1 ? "" : "s"}. Deleting it will PERMANENTLY remove the category AND all ${n} of its videos. This cannot be undone.\n\nAre you sure you want to continue?`
      : `Permanently delete the category "${c.name}"? This cannot be undone.`;
    if (!window.confirm(warning)) return;
    try {
      const { data } = await api.delete(`/categories/${c.slug}`);
      toast.success(`Category deleted${data.deleted_videos ? ` (${data.deleted_videos} videos removed)` : ""}`);
      load();
    } catch { toast.error("Delete failed"); }
  };
  return (
    <div data-testid="admin-categories">
      <h2 className="font-serif text-2xl text-bone mb-2 flex items-center gap-2"><Layers size={22} className="text-gold" /> Categories</h2>
      <p className="text-bone/50 text-sm mb-6">These are the video categories shown across the site. Deleting a category also removes every video inside it — you'll be warned first.</p>
      {loading ? (
        <div className="text-bone/50 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading...</div>
      ) : cats.length === 0 ? (
        <p className="text-bone/50 text-sm" data-testid="admin-categories-empty">No categories.</p>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10 max-w-2xl">
          {cats.map((c) => (
            <div key={c.slug} className="flex items-center gap-3 p-4 bg-surface text-sm" data-testid="admin-category-row">
              <div className="flex-1 min-w-0">
                <p className="text-bone truncate">{c.name}</p>
                <p className="text-bone/40 text-xs">
                  {c.video_count > 0
                    ? <span className="text-amber-300/80 inline-flex items-center gap-1"><AlertTriangle size={11} /> {c.video_count} video{c.video_count === 1 ? "" : "s"} inside</span>
                    : "empty"}
                </p>
              </div>
              <button onClick={() => del(c)} className="text-bone/50 hover:text-red-400 flex items-center gap-1.5 border border-white/10 hover:border-red-400/40 px-3 py-1.5 transition-colors" data-testid="admin-category-delete">
                <Trash2 size={14} /> Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HealingForm() {
  return (
    <ContentManager
      endpoint="/healing"
      heading={<><HeartPulse size={22} className="text-gold" /> Health & Healing Resources</>}
      description="Curated wellness resources shown on the Health & Healing page. Add, edit or remove any."
      testid="admin-healing"
      blank={{ section: "Ancestral & Herbal Medicine", title: "", description: "", source_label: "", url: "", featured: "false" }}
      fields={[
        { key: "section", label: "Section", type: "select", full: true, options: ["Ancestral & Herbal Medicine", "Mind, Spirit & Emotional Wellness", "Food as Medicine & Nutrition", "Movement, Breath & Body"] },
        { key: "title", label: "Title", required: true },
        { key: "source_label", label: "Source Label", placeholder: "Oldways, Therapy for Black Girls..." },
        { key: "url", label: "Resource URL", full: true, required: true, placeholder: "https://..." },
        { key: "description", label: "Description", type: "textarea" },
        { key: "featured", label: "Featured", type: "select", options: [["false", "No"], ["true", "Yes"]] },
      ]}
      toPayload={(f) => ({ ...f, featured: f.featured === true || f.featured === "true" })}
      fromItem={(it) => ({ ...it, featured: it.featured ? "true" : "false" })}
      primary={(it) => it.title}
      secondary={(it) => `${it.section} · ${it.source_label || ""}`}
    />
  );
}

function ProductForm() {
  return (
    <ContentManager
      endpoint="/products"
      heading={<><ShoppingBag size={22} className="text-gold" /> Store Products</>}
      description="Add direct-sale (Stripe) or referral products. Edit or remove any product below."
      testid="admin-product"
      blank={{ name: "", type: "own", price: "", category: "", description: "", referral_url: "", store: "Amazon", image_color: "from-amber-700 to-yellow-900" }}
      fields={[
        { key: "name", label: "Product Name", required: true },
        { key: "type", label: "Type", type: "select", options: [["own", "Direct Sale (Stripe)"], ["referral", "Referral Link"]] },
        { key: "price", label: "Price (USD)", type: "number", required: true, showIf: (f) => f.type === "own" },
        { key: "referral_url", label: "Referral URL", required: true, showIf: (f) => f.type === "referral" },
        { key: "store", label: "Store", type: "select", options: ["Amazon", "eBay", "Alibaba"], showIf: (f) => f.type === "referral" },
        { key: "category", label: "Category", placeholder: "Natural & Earth Products" },
        { key: "description", label: "Description", type: "textarea" },
      ]}
      toPayload={(f) => ({
        name: f.name, type: f.type, category: f.category, description: f.description, image_color: f.image_color,
        price: f.type === "own" ? parseFloat(f.price) : null,
        referral_url: f.type === "referral" ? f.referral_url : null,
        store: f.type === "referral" ? f.store : null,
      })}
      fromItem={(it) => ({
        name: it.name || "", type: it.type || "own", price: it.price ?? "", category: it.category || "",
        description: it.description || "", referral_url: it.referral_url || "", store: it.store || "Amazon",
        image_color: it.image_color || "from-amber-700 to-yellow-900",
      })}
      primary={(it) => it.name}
      secondary={(it) => it.type === "own" ? `$${it.price} · Direct` : `Referral · ${it.store}`}
    />
  );
}

function ClassForm() {
  return (
    <ContentManager
      endpoint="/classes"
      heading={<><GraduationCap size={22} className="text-gold" /> Classes & Training</>}
      description="Add, edit or remove learning paths. Choose which section of the Classes page each appears in."
      testid="admin-class"
      blank={{ group: "University & Diaspora Studies", organization: "", title: "", level: "Beginner", lessons: 8, duration: "", description: "", sourcesText: "" }}
      fields={[
        { key: "group", label: "Section", type: "select", options: ["University & Diaspora Studies", "Cultural Institutions", "Online Courses", "Esoteric Schools", "Other Alternative Teachings"], full: true, hint: "Where this class appears on the Classes page." },
        { key: "organization", label: "Organization", required: true },
        { key: "title", label: "Title", required: true },
        { key: "level", label: "Level", type: "select", options: ["Beginner", "Intermediate", "Advanced"] },
        { key: "lessons", label: "Lessons", type: "number" },
        { key: "duration", label: "Duration", placeholder: "6 weeks" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "sourcesText", label: "Original Sources (one per line)", type: "textarea", rows: 4, placeholder: "https://noi.org\nMessage to the Blackman | https://store.finalcall.com/... | text", hint: "Simplest: paste one link per line. Optional format for a nicer label: Label | URL | kind (kind = official, text, or reference).",
          preview: (val) => {
            const parsed = parseSources(val);
            if (!parsed.length) return null;
            return (
              <div className="mt-3 border border-white/10 bg-black/30 p-3 space-y-1.5" data-testid="admin-class-sources-preview">
                <p className="text-[10px] uppercase tracking-widest text-gold/70">Links that will be saved ({parsed.length})</p>
                {parsed.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <CheckCircle2 size={13} className="text-emerald-400 shrink-0" />
                    <span className="text-bone/40 uppercase tracking-widest text-[10px] w-16 shrink-0">{s.kind}</span>
                    <a href={s.url} target="_blank" rel="noreferrer" className="text-gold hover:underline truncate">{s.label}</a>
                  </div>
                ))}
              </div>
            );
          } },
      ]}
      toPayload={(f) => ({
        group: f.group, organization: f.organization, title: f.title, level: f.level, duration: f.duration,
        description: f.description, lessons: parseInt(f.lessons) || 0, sources: parseSources(f.sourcesText),
      })}
      fromItem={(it) => ({
        group: it.group || "University & Diaspora Studies", organization: it.organization || "", title: it.title || "",
        level: it.level || "Beginner", lessons: it.lessons || 8, duration: it.duration || "", description: it.description || "",
        sourcesText: (it.sources || []).map((s) => `${s.label} | ${s.url} | ${s.kind || "reference"}`).join("\n"),
      })}
      primary={(it) => it.title}
      secondary={(it) => `${it.organization} · ${it.group}`}
    />
  );
}

function LinkHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/link-health"); setData(data); }
    catch { toast.error("Failed to load link health"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/link-check");
      toast.success(`Checked ${data.checked} links — ${data.broken} broken`);
      await load();
    } catch { toast.error("Check failed"); }
    finally { setChecking(false); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

  return (
    <div data-testid="admin-link-health">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="font-serif text-2xl text-bone flex items-center gap-2"><ShieldCheck size={22} className="text-gold" /> Source Link Health</h2>
          <p className="text-bone/50 text-sm mt-1">Automatically re-checked every 30 days. Broken links are flagged so your Verified badges stay trustworthy.</p>
        </div>
        <button onClick={runCheck} disabled={checking} data-testid="run-link-check"
          className="bg-gold text-black px-6 py-2.5 font-medium hover:bg-gold2 transition-colors flex items-center gap-2 disabled:opacity-60">
          <RefreshCw size={16} className={checking ? "animate-spin" : ""} /> {checking ? "Checking..." : "Run Check Now"}
        </button>
      </div>

      {loading ? (
        <p className="text-bone/40 py-16 text-center">Loading report...</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Stat label="Total Links" value={data.total} />
            <Stat label="Healthy" value={data.total - data.broken} good />
            <Stat label="Broken" value={data.broken} bad={data.broken > 0} />
          </div>
          <div className="border border-white/10 divide-y divide-white/10">
            {data.items.map((it, i) => (
              <div key={i} className="flex items-center gap-4 p-4 bg-surface" data-testid="link-health-row">
                {it.status === "broken"
                  ? <AlertTriangle size={18} className="text-red-400 shrink-0" />
                  : <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-bone text-sm truncate">{it.label || it.url}</p>
                  <p className="text-bone/40 text-xs truncate">{it.class ? `${it.class} · ` : ""}{it.url}</p>
                </div>
                <span className={`text-xs font-mono ${it.status === "broken" ? "text-red-400" : "text-bone/40"}`}>
                  {it.status_code ?? "ERR"}
                </span>
                <span className="text-bone/30 text-xs hidden md:block w-40 text-right">{fmt(it.checked_at)}</span>
                <a href={it.url} target="_blank" rel="noreferrer" className="text-bone/40 hover:text-gold"><ExternalLink size={15} /></a>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, good, bad }) {
  return (
    <div className={`border p-5 bg-surface ${bad ? "border-red-400/40" : good ? "border-emerald-400/30" : "border-white/10"}`}>
      <div className={`font-serif text-4xl ${bad ? "text-red-400" : good ? "text-emerald-400" : "text-gold"}`}>{value}</div>
      <div className="text-xs uppercase tracking-widest text-bone/50 mt-1">{label}</div>
    </div>
  );
}


function ArticleForm() {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ title: "", tag: "News", date: today, summary: "", content: "", image_url: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [events, setEvents] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => { setF({ title: "", tag: "News", date: today, summary: "", content: "", image_url: "" }); setEditingId(null); };

  const onImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Image uploaded");
    } catch { toast.error("Image upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editingId) { await api.put(`/events/${editingId}`, f); toast.success("Article updated"); }
      else { await api.post("/events", f); toast.success("Article published — newest date becomes the headline."); }
      reset();
      load();
    } catch { toast.error(editingId ? "Failed to update" : "Failed to publish"); } finally { setBusy(false); }
  };

  const edit = (ev) => {
    setF({ title: ev.title || "", tag: ev.tag || "News", date: ev.date || today, summary: ev.summary || "", content: ev.content || "", image_url: ev.image_url || "" });
    setEditingId(ev.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/events/${id}`); toast.success("Deleted"); if (editingId === id) reset(); load(); }
    catch { toast.error("Delete failed"); }
  };

  const sorted = [...events].sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  return (
    <div data-testid="admin-article">
      <h2 className="font-serif text-2xl text-bone mb-2 flex items-center gap-2"><Newspaper size={22} className="text-gold" /> Post an Article / Event</h2>
      <p className="text-bone/50 text-sm mb-6">Publish, edit or remove current events. The article with the most recent date automatically becomes the rotating Headline on the homepage.</p>

      <form onSubmit={submit} className="bg-surface border border-white/10 p-8 space-y-5 max-w-2xl mb-10" data-testid="admin-article-form">
        {editingId && (
          <div className="flex items-center justify-between bg-gold/10 border border-gold/30 px-4 py-2 text-sm text-gold">
            <span>Editing existing article</span>
            <button type="button" onClick={reset} className="hover:text-bone" data-testid="article-cancel-edit">Cancel</button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div><label className={lbl}>Title</label><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={field} data-testid="article-title" /></div>
          <div><label className={lbl}>Tag</label><input value={f.tag} onChange={(e) => setF({ ...f, tag: e.target.value })} className={field} placeholder="Movement / Economics / Kemet..." data-testid="article-tag" /></div>
        </div>
        <div><label className={lbl}>Date</label><input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={field} data-testid="article-date" /></div>
        <div><label className={lbl}>Summary (one line)</label><input required value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} className={field} data-testid="article-summary" /></div>
        <div>
          <label className={lbl}>Article Image (optional)</label>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 border border-white/15 text-bone/80 px-4 py-2 text-sm cursor-pointer hover:border-gold/40 transition-colors" data-testid="article-image-upload-label">
              <Upload size={15} /> {uploading ? "Uploading..." : "Upload Image"}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onImage} className="hidden" data-testid="article-image-file" disabled={uploading} />
            </label>
            {f.image_url && (
              <div className="flex items-center gap-2">
                <img src={f.image_url} alt="preview" className="h-12 w-20 object-cover border border-white/10" data-testid="article-image-preview" />
                <button type="button" onClick={() => setF({ ...f, image_url: "" })} className="text-bone/40 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            )}
          </div>
          <input value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} className={`${field} mt-2`} placeholder="...or paste an image URL" data-testid="article-image" />
        </div>
        <div><label className={lbl}>Full Article</label><textarea required value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} className={field} rows={6} data-testid="article-content" /></div>
        <SubmitBtn busy={busy} label={editingId ? "Save Changes" : "Publish Article"} testid="article-submit" />
      </form>

      <h3 className="font-serif text-xl text-bone mb-3">Published Articles ({sorted.length})</h3>
      <div className="border border-white/10 divide-y divide-white/10">
        {sorted.map((e, i) => (
          <div key={e.id} className="flex items-center gap-3 p-3 bg-surface text-sm" data-testid="article-row">
            {i === 0 && <span className="text-[10px] uppercase tracking-widest text-black bg-gold px-2 py-0.5">Headline</span>}
            <span className="text-bone/40 font-mono text-xs w-24">{e.date}</span>
            <span className="text-bone flex-1 truncate">{e.title}</span>
            <span className="text-bone/40 text-xs">{e.tag}</span>
            <button onClick={() => edit(e)} className="text-bone/40 hover:text-gold" data-testid="article-edit"><Pencil size={15} /></button>
            <button onClick={() => del(e.id)} className="text-bone/40 hover:text-red-400" data-testid="article-delete"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function VerifyCredentials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/opportunities"); setItems(data); }
    catch { toast.error("Failed to load"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    let note = "";
    if (status === "unverified") note = window.prompt("Reason credentials could not be verified (shown to poster):", "Business registration could not be confirmed.") || "";
    try {
      await api.post(`/opportunities/${id}/verify`, { status, note });
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const StatusChip = ({ s }) => {
    if (s === "verified") return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-400/30 px-2 py-0.5"><CheckCircle2 size={12} /> Verified</span>;
    if (s === "unverified") return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-300 border border-amber-400/40 px-2 py-0.5"><Star size={12} fill="currentColor" /> Unverified</span>;
    return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-bone/50 border border-white/15 px-2 py-0.5"><Clock size={12} /> Pending</span>;
  };

  return (
    <div data-testid="admin-verify">
      <h2 className="font-serif text-2xl text-bone mb-2 flex items-center gap-2"><Globe2 size={22} className="text-gold" /> Verify Business Credentials</h2>
      <p className="text-bone/50 text-sm mb-6">Review submitted opportunities. Mark as verified once credentials check out, or flag as unverified (displays a yellow-star warning to visitors).</p>
      {loading ? <p className="text-bone/40 py-12 text-center">Loading...</p> : (
        <div className="space-y-4">
          {items.map((o) => (
            <div key={o.id} className="border border-white/10 bg-surface p-5" data-testid="verify-row">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <span className="text-bone font-medium">{o.title}</span>
                <StatusChip s={o.verification_status} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-bone/60 mb-4">
                <span><span className="text-bone/40">Business:</span> {o.business_name}</span>
                <span><span className="text-bone/40">Reg No:</span> {o.business_registration || "— none provided —"}</span>
                <span><span className="text-bone/40">Country:</span> {o.country}{o.is_international ? " · International" : ""}</span>
                <span><span className="text-bone/40">Contact:</span> {o.contact_name} · {o.contact_email}</span>
                {o.website && <span className="md:col-span-2"><span className="text-bone/40">Website:</span> <a href={o.website} target="_blank" rel="noreferrer" className="text-gold">{o.website}</a></span>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setStatus(o.id, "verified")} data-testid="verify-approve" className="bg-emerald-500/90 text-black px-4 py-2 text-sm font-medium hover:bg-emerald-400 transition-colors flex items-center gap-2"><CheckCircle2 size={15} /> Verify</button>
                <button onClick={() => setStatus(o.id, "unverified")} data-testid="verify-flag" className="bg-amber-500/90 text-black px-4 py-2 text-sm font-medium hover:bg-amber-400 transition-colors flex items-center gap-2"><Star size={15} fill="currentColor" /> Flag Unverified</button>
                <button onClick={() => setStatus(o.id, "pending")} className="border border-white/15 text-bone/70 px-4 py-2 text-sm hover:border-gold/40 transition-colors">Reset to Pending</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Subscribers() {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [data, setData] = useState({ total: 0, subscribers: [] });

  const load = async () => {
    try { const { data } = await api.get("/newsletter"); setData(data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setRaw((prev) => (prev ? prev + "\n" : "") + ev.target.result);
    reader.readAsText(file);
  };

  const doImport = async () => {
    if (!raw.trim()) return toast.error("Paste or upload contacts first");
    setBusy(true);
    try {
      const { data: r } = await api.post("/newsletter/import", { raw, source: "manual-import" });
      setResult(r);
      toast.success(`${r.added} added · ${r.skipped_existing} already subscribed · ${r.invalid} invalid`);
      setRaw("");
      load();
    } catch { toast.error("Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-subscribers">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h2 className="font-serif text-2xl text-bone flex items-center gap-2"><Mail size={22} className="text-gold" /> Newsletter Subscribers</h2>
        <span className="text-bone/50 text-sm font-mono">{data.total} total</span>
      </div>
      <p className="text-bone/50 text-sm mb-6">Import a consented contact list (CSV or pasted). Accepts one per line: <span className="font-mono text-bone/70">email</span>, <span className="font-mono text-bone/70">name,email</span>, or <span className="font-mono text-bone/70">Name &lt;email&gt;</span>. Duplicates are skipped automatically. Only import contacts who have opted in.</p>

      <div className="bg-surface border border-white/10 p-6 mb-8 space-y-4">
        <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={7} data-testid="import-textarea"
          className="w-full bg-black/50 border border-white/10 px-4 py-3 text-bone text-sm font-mono focus:outline-none focus:border-gold/50"
          placeholder={"jane@example.com\nKofi Mensah, kofi@example.com\nAma Serwaa <ama@example.com>"} />
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 border border-white/15 text-bone/80 px-4 py-2 text-sm cursor-pointer hover:border-gold/40 transition-colors" data-testid="import-file-label">
            <Upload size={15} /> Upload CSV
            <input type="file" accept=".csv,.txt" onChange={onFile} className="hidden" data-testid="import-file" />
          </label>
          <button onClick={doImport} disabled={busy} data-testid="import-submit"
            className="bg-gold text-black px-6 py-2 text-sm font-medium hover:bg-gold2 transition-colors flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 size={15} className="animate-spin" />} Import Contacts
          </button>
        </div>
        {result && (
          <div className="text-sm text-bone/70 border-t border-white/10 pt-4" data-testid="import-result">
            <span className="text-emerald-300">{result.added} added</span> · {result.skipped_existing} already subscribed · <span className="text-amber-300">{result.invalid} invalid</span>
            {result.invalid_samples?.length > 0 && <p className="text-bone/40 text-xs mt-1">Invalid: {result.invalid_samples.join(", ")}</p>}
          </div>
        )}
      </div>

      <div className="border border-white/10 divide-y divide-white/10 max-h-96 overflow-y-auto">
        {data.subscribers.length === 0 ? (
          <p className="text-bone/40 text-sm p-5 text-center">No subscribers yet.</p>
        ) : data.subscribers.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-3 p-3 bg-surface text-sm" data-testid="subscriber-row">
            <span className="text-bone">{s.name || <span className="text-bone/40">—</span>}</span>
            <span className="text-bone/60 flex-1 text-center truncate">{s.email}</span>
            <span className="text-bone/30 text-xs">{s.source || "signup"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SubmitBtn({ busy, label, testid }) {
  return (
    <button type="submit" disabled={busy} data-testid={testid}
      className="bg-gold text-black px-8 py-3 font-medium hover:bg-gold2 transition-colors flex items-center gap-2 disabled:opacity-60">
      {busy && <Loader2 size={16} className="animate-spin" />} {label}
    </button>
  );
}

function ChangePassword() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (f.new_password !== f.confirm) { toast.error("New password and confirmation do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password updated successfully");
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update password");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-surface border border-white/10 p-8 space-y-5 max-w-lg" data-testid="admin-password-form">
      <div className="flex items-center gap-2 text-gold"><Lock size={18} /><span className="text-sm uppercase tracking-widest">Account Security</span></div>
      <p className="text-bone/50 text-sm">Update your admin password. Choose a strong password of at least 8 characters. You'll stay signed in after the change.</p>
      <div><label className={lbl}>Current Password</label><input type="password" required value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className={field} data-testid="admin-current-password" /></div>
      <div><label className={lbl}>New Password</label><input type="password" required minLength={8} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className={field} data-testid="admin-new-password" /></div>
      <div><label className={lbl}>Confirm New Password</label><input type="password" required value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className={field} data-testid="admin-confirm-password" /></div>
      <SubmitBtn busy={busy} label="Update Password" testid="admin-password-submit" />
    </form>
  );
}

function TickerForm() {
  const [text, setText] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [showNews, setShowNews] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/ticker?raw=true").then((r) => {
      setText((r.data.messages || []).join("\n"));
      setEnabled(r.data.enabled !== false);
      setShowNews(r.data.show_news !== false);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const messages = text.split("\n").map((l) => l.trim()).filter(Boolean);
      await api.put("/ticker", { messages, enabled, show_news: showNews });
      toast.success("Ticker updated");
    } catch { toast.error("Failed to update ticker"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-surface border border-white/10 p-8 space-y-5 max-w-2xl" data-testid="admin-ticker-form">
      <div className="flex items-center gap-2 text-gold"><Megaphone size={18} /><span className="text-sm uppercase tracking-widest">Home Page Ticker</span></div>
      <p className="text-bone/50 text-sm">These messages scroll across the tickertape just below the banner on the home page. Enter one highlight per line.</p>
      <label className="flex items-center gap-2 text-sm text-bone/80 cursor-pointer select-none" data-testid="admin-ticker-enabled">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="accent-gold w-4 h-4" />
        Show ticker on home page
      </label>
      <label className="flex items-center gap-2 text-sm text-bone/80 cursor-pointer select-none" data-testid="admin-ticker-shownews">
        <input type="checkbox" checked={showNews} onChange={(e) => setShowNews(e.target.checked)} className="accent-gold w-4 h-4" />
        Auto-show latest Articles &amp; Events (newest 3 headlines lead the ticker)
      </label>
      <div>
        <label className={lbl}>Your Highlight Messages (one per line)</label>
        <textarea value={text} onChange={(e) => setText(e.target.value)} className={field} rows={7} disabled={!loaded}
          placeholder={"330+ curated lectures across 10 sacred categories\nAsk the Oracle — your AI guide to the teachings\nJoin the Global Diaspora Network"} data-testid="admin-ticker-text" />
        <p className="text-bone/40 text-xs mt-1">When auto-news is on, the 3 most recent Article/Event headlines appear before these messages automatically.</p>
      </div>
      <SubmitBtn busy={busy} label="Save Ticker" testid="admin-ticker-submit" />
    </form>
  );
}


function CommunityForm() {
  const FIELDS = [
    ["whatsapp", "WhatsApp Community / Group", "https://chat.whatsapp.com/XXXXXXXX"],
    ["telegram", "Telegram Channel / Group", "https://t.me/apocalypz"],
    ["discord", "Discord Invite (optional)", "https://discord.gg/XXXX"],
    ["facebook", "Facebook Group / Page (optional)", "https://facebook.com/groups/XXXX"],
    ["instagram", "Instagram (optional)", "https://instagram.com/apocalypz"],
  ];
  const [f, setF] = useState({ whatsapp: "", telegram: "", discord: "", facebook: "", instagram: "" });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/settings/community").then((r) => { setF((p) => ({ ...p, ...r.data })); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put("/settings/community", f);
      toast.success("Community links updated");
    } catch { toast.error("Failed to save community links"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-surface border border-white/10 p-8 space-y-5 max-w-2xl" data-testid="admin-community-form">
      <div className="flex items-center gap-2 text-gold"><Share2 size={18} /><span className="text-sm uppercase tracking-widest">Footer Community Links</span></div>
      <p className="text-bone/50 text-sm">Paste your real invite links. WhatsApp and Telegram buttons always show in the footer; the others appear only when a link is set. Leave blank to show a friendly "coming soon" message.</p>
      {FIELDS.map(([k, label, ph]) => (
        <div key={k}>
          <label className={lbl}>{label}</label>
          <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={field} placeholder={ph}
            disabled={!loaded} data-testid={`admin-community-${k}`} />
        </div>
      ))}
      <SubmitBtn busy={busy} label="Save Community Links" testid="admin-community-submit" />
    </form>
  );
}


function NotificationsForm() {
  const [cfg, setCfg] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [frequency, setFrequency] = useState("daily");
  const [busy, setBusy] = useState(false);
  const [regen, setRegen] = useState(false);
  const load = () => api.get("/notifications/config").then((r) => {
    setCfg(r.data);
    setRecipients((r.data.recipients || []).join("\n"));
    setFrequency(r.data.frequency || "daily");
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const tenderUrl = cfg ? `${window.location.origin}/circle-tender?key=${cfg.secret}` : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(tenderUrl); toast.success("Tender link copied"); }
    catch { toast.error("Could not copy"); }
  };
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const recips = recipients.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
      const r = await api.put("/notifications/config", { recipients: recips, frequency });
      const saved = r.data.recipients || [];
      if (saved.length < recips.length)
        toast.warning(`Saved. ${recips.length - saved.length} invalid or duplicate email(s) were removed.`);
      else
        toast.success("Reply alerts updated");
      load();
    } catch { toast.error("Failed to save"); } finally { setBusy(false); }
  };
  const regenerate = async () => {
    if (!window.confirm("Regenerate the secret link? The old link will stop working immediately.")) return;
    setRegen(true);
    try { const r = await api.post("/notifications/regenerate-key"); setCfg((c) => ({ ...c, secret: r.data.secret })); toast.success("New link generated"); }
    catch { toast.error("Failed to regenerate"); } finally { setRegen(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <form onSubmit={save} className="bg-surface border border-white/10 p-8 space-y-5" data-testid="admin-notifications-form">
        <div className="flex items-center gap-2 text-gold"><Bell size={18} /><span className="text-sm uppercase tracking-widest">Forum Reply Digest</span></div>
        <p className="text-bone/50 text-sm">Email a digest of new Community Forum replies to the addresses below. A daily digest sends once every 24h; weekly once every 7 days.</p>
        <div>
          <label className={lbl}>Frequency</label>
          <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className={field} data-testid="admin-notif-frequency">
            <option value="daily">Daily digest</option>
            <option value="weekly">Weekly digest</option>
            <option value="off">Paused (no emails)</option>
          </select>
        </div>
        <div>
          <label className={lbl}>Recipient emails (one per line)</label>
          <textarea value={recipients} onChange={(e) => setRecipients(e.target.value)} rows={4} className={field}
            placeholder={"you@example.com\ntender@example.com"} data-testid="admin-notif-recipients" />
          <p className="text-bone/40 text-xs mt-1">Leave empty to disable sending. {cfg?.last_sent_at ? `Last digest: ${new Date(cfg.last_sent_at).toLocaleString()}` : "No digest sent yet."}</p>
        </div>
        <SubmitBtn busy={busy} label="Save Reply Alerts" testid="admin-notif-submit" />
      </form>

      <div className="bg-surface border border-white/10 p-8 space-y-4" data-testid="admin-tender-link-box">
        <div className="flex items-center gap-2 text-gold"><KeyRound size={18} /><span className="text-sm uppercase tracking-widest">Delegate Link (no admin login)</span></div>
        <p className="text-bone/50 text-sm">Share this secret link with a trusted "circle tender". They can change the frequency and recipient list from a simple page — without accessing this Admin panel. Anyone with the link can manage it, so share carefully.</p>
        <div className="flex items-stretch gap-2">
          <input readOnly value={tenderUrl} className={`${field} font-mono text-xs`} data-testid="admin-tender-url" onFocus={(e) => e.target.select()} />
          <button type="button" onClick={copy} className="shrink-0 inline-flex items-center gap-2 border border-white/15 text-bone/80 px-4 hover:border-gold/50 hover:text-gold transition-colors" data-testid="admin-tender-copy"><Copy size={15} /> Copy</button>
        </div>
        <button type="button" onClick={regenerate} disabled={regen} data-testid="admin-tender-regenerate"
          className="inline-flex items-center gap-2 text-sm text-bone/60 hover:text-gold transition-colors disabled:opacity-50">
          {regen ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Regenerate link (revokes the old one)
        </button>
      </div>
    </div>
  );
}


const CAMPAIGN_TABS_LABEL = { visit: "Visits", newsletter_signup: "Signups", guide_download: "Guide downloads", oracle_opened: "Oracle opens", share_click: "Shares", checkout_start: "Checkouts", feedback_submitted: "Feedback" };

function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const load = (d) => api.get(`/analytics?days=${d}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(days); }, [days]);
  if (!data) return <div className="text-bone/50 flex items-center gap-2" data-testid="analytics-loading"><Loader2 size={16} className="animate-spin" /> Loading analytics...</div>;
  const Stat = ({ label, value, testid }) => (
    <div className="bg-surface border border-white/10 p-5" data-testid={testid}>
      <p className="text-3xl font-serif text-gold">{value}</p>
      <p className="text-bone/50 text-xs uppercase tracking-widest mt-1">{label}</p>
    </div>
  );
  const Bars = ({ title, rows, testid }) => {
    const max = Math.max(1, ...rows.map((r) => r.count));
    return (
      <div className="bg-surface border border-white/10 p-6" data-testid={testid}>
        <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">{title}</p>
        {rows.length === 0 ? <p className="text-bone/40 text-sm">No data yet.</p> : (
          <div className="space-y-2.5">
            {rows.slice(0, 8).map((r) => (
              <div key={r.key} className="flex items-center gap-3">
                <span className="text-bone/70 text-sm w-32 truncate capitalize">{CAMPAIGN_TABS_LABEL[r.key] || r.key}</span>
                <div className="flex-1 bg-black/40 h-4 rounded-sm overflow-hidden">
                  <div className="h-full bg-gold/70" style={{ width: `${(r.count / max) * 100}%` }} />
                </div>
                <span className="text-bone/60 text-sm tabular-nums w-10 text-right">{r.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  return (
    <div className="space-y-6" data-testid="analytics-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-gold"><BarChart3 size={18} /><span className="text-sm uppercase tracking-widest">Campaign Analytics</span></div>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`analytics-range-${d}`}
              className={`px-3 py-1.5 text-sm ${days === d ? "bg-gold text-black" : "border border-white/15 text-bone/70 hover:border-gold/40"}`}>{d}d</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Visits" value={data.visits} testid="stat-visits" />
        <Stat label="Unique Visitors" value={data.unique_visitors} testid="stat-unique" />
        <Stat label="Total Events" value={data.total_events} testid="stat-events" />
        <Stat label="Signups" value={(data.events.find((e) => e.key === "newsletter_signup") || {}).count || 0} testid="stat-signups" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Bars title="Actions by Type" rows={data.events} testid="bars-events" />
        <Bars title="Traffic by Source (UTM)" rows={data.sources} testid="bars-sources" />
        <Bars title="Top Campaigns" rows={data.campaigns} testid="bars-campaigns" />
        <Bars title="By Medium" rows={data.mediums} testid="bars-mediums" />
      </div>
      <div className="bg-surface border border-white/10 p-6 overflow-x-auto" data-testid="source-performance">
        <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Source Effectiveness</p>
        <table className="w-full text-sm">
          <thead><tr className="text-bone/40 text-xs uppercase tracking-widest text-left">
            <th className="pb-2">Source</th><th className="pb-2 text-right">Visits</th><th className="pb-2 text-right">Signups</th><th className="pb-2 text-right">Downloads</th><th className="pb-2 text-right">Shares</th><th className="pb-2 text-right">Conv. %</th>
          </tr></thead>
          <tbody>
            {data.source_performance.map((s) => (
              <tr key={s.source} className="border-t border-white/5 text-bone/75">
                <td className="py-2 capitalize">{s.source}</td>
                <td className="py-2 text-right tabular-nums">{s.visits}</td>
                <td className="py-2 text-right tabular-nums">{s.signups}</td>
                <td className="py-2 text-right tabular-nums">{s.downloads}</td>
                <td className="py-2 text-right tabular-nums">{s.shares}</td>
                <td className="py-2 text-right tabular-nums text-gold">{s.visits ? Math.round((s.signups / s.visits) * 100) : 0}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-bone/40 text-xs">Tip: add <code className="text-gold/70">?utm_source=whatsapp&amp;utm_medium=social&amp;utm_campaign=each_one_reach_one</code> to the links you share so each channel shows up here.</p>
    </div>
  );
}

function FeedbackPanel() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/feedback").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const markReviewed = async (id) => { await api.post(`/feedback/${id}/reviewed`); load(); };
  if (!data) return <div className="text-bone/50 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading feedback...</div>;
  const items = data.items.filter((i) => filter === "all" || i.category === filter);
  const CATLABEL = { content: "Content", market: "New Market", general: "General" };
  return (
    <div className="space-y-5" data-testid="feedback-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-gold"><MessageSquare size={18} /><span className="text-sm uppercase tracking-widest">Visitor Feedback · {data.counts.new} new</span></div>
        <div className="flex gap-2">
          {["all", "content", "market", "general"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`feedback-filter-${f}`}
              className={`px-3 py-1.5 text-sm capitalize ${filter === f ? "bg-gold text-black" : "border border-white/15 text-bone/70 hover:border-gold/40"}`}>{f === "market" ? "Markets" : f}</button>
          ))}
        </div>
      </div>
      {items.length === 0 ? <p className="text-bone/50" data-testid="feedback-empty">No feedback yet.</p> : (
        <div className="space-y-3">
          {items.map((f) => (
            <div key={f.id} className={`bg-surface border p-5 ${f.status === "new" ? "border-gold/30" : "border-white/10"}`} data-testid="feedback-item">
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest bg-gold/15 text-gold px-2 py-0.5 rounded-sm">{CATLABEL[f.category] || f.category}</span>
                  {f.market && <span className="text-[10px] uppercase tracking-widest border border-white/15 text-bone/60 px-2 py-0.5 rounded-sm">{f.market}</span>}
                  {f.status === "new" && <span className="text-[10px] uppercase tracking-widest text-emerald-300">● new</span>}
                </div>
                <span className="text-bone/40 text-xs">{(f.created_at || "").slice(0, 10)}</span>
              </div>
              <p className="text-bone/85 text-sm leading-relaxed whitespace-pre-wrap">{f.message}</p>
              <div className="flex items-center justify-between mt-3">
                {f.email ? <a href={`mailto:${f.email}`} className="text-gold/70 text-xs hover:text-gold">{f.email}</a> : <span className="text-bone/30 text-xs">no email</span>}
                {f.status === "new" && <button onClick={() => markReviewed(f.id)} data-testid="feedback-mark-reviewed" className="text-xs border border-white/15 text-bone/70 px-3 py-1 hover:border-gold/40 hover:text-gold transition-colors">Mark reviewed</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
